import React, {useEffect, useState, Fragment, useMemo} from "react";
import {connect} from "react-redux";
import {translate} from '@elements/translations';
import Loading from "../../shared/components/Loading"
import {changeListDate, requireData} from "../actions/actions";
import {getListFetchingState, getListItems, getListAvailableCategories, getListStartDate, getListEndDate} from "../reducer/rootReducer";
import {FETCHING_STATE, formatDate, noop} from "../../shared/utils";
import TableRowItem from "./TableRowItem";
import Pagination from "../../shared/components/Pagination";
import Flatpickr from "react-flatpickr";
import Select from 'react-select'

const Base = ({fetchingState, items, availableCategories, listStartDate, listEndDate, onLoadData = noop, onChangeListDate = noop}) => {
    const [filter, setFilter] = useState({
        categories: [],
        search: ''
    });

    useEffect(function () {
        if (!fetchingState) {
            onLoadData({startDate: listStartDate, endDate: listEndDate})
        }
    }, [fetchingState]);


    const filteredItems = items.filter(item => {
        const search = filter.search.trim().toLowerCase();

        let invoicesNotes = [...item.referencedDeliveryNotes, ...item.referencedInvoices, ...item.referencedPurchaseOrders]

        const matchesSearch = search ? (
            item.number && item.number.toLowerCase().includes(search)) ||
            (item.reference && item.reference.toLowerCase().includes(search)) ||
            item.referencedDeliveryNotes.some(a => a.toLowerCase().includes(search)) ||
            item.referencedInvoices.some(a => a.toLowerCase().includes(search)) ||
            item.referencedPurchaseOrders.toLowerCase().includes(search) ||
            invoicesNotes.some(a => a.includes(search))
            : true;


        const subItemsMatch = item.items.some(subItem => {
            const joinedDescription = `${subItem.description1 || ''} ${subItem.description2 || ''}`.toLowerCase();

            return (subItem.productId && subItem.productId.toLowerCase().includes(search)) ||
                joinedDescription.includes(search) ||
                (subItem.productName && subItem.productName.toLowerCase().includes(search));
        });

        const matchesCategory = filter.categories.length
            ? filter.categories.includes(item.submitType)
            : true;

        return (matchesSearch || subItemsMatch) && matchesCategory
    });

    const itemsPerPage = 20;
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    const handleSorting = (field) => {
        setSortField(field);
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    const sortedItems = sortField ? [...filteredItems].sort((a, b) => {
            let result = 0;
            if (sortField === 'date') {
                result = new Date(a.date) - new Date(b.date);
            } else {
                const aValue = (a[sortField] || '').toString();
                const bValue = (b[sortField] || '').toString();
                result = aValue.localeCompare(bValue);
            }
            return sortDirection === 'asc' ? result : -result;
        }) : filteredItems;

    const totalPages = Math.ceil(sortedItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentPageItems = sortedItems.slice(startIndex, startIndex + itemsPerPage);

    const handleChangePage = (page) => {
        setCurrentPage(page);
    }

    const renderDateQuickInputs = (container, instance) => {
        const dateQuickInputs = document.createElement('div');
        dateQuickInputs.className = 'd-flex flex-wrap gap-1 mt-2 p-2';

        const buttons = [
            { label: translate('datepicker.quick-input.365'), action: 'last-365-days' },
            { label: translate('datepicker.quick-input.this-year'), action: 'this-year' },
            { label: translate('datepicker.quick-input.last-year'), action: 'last-year' },
            { label: translate('datepicker.quick-input.year-before-last'), action: 'year-before-last' }
        ];

        dateQuickInputs.innerHTML = buttons.map(button => `
            <button class="btn btn-sm btn-light-grey-outline" data-action="${button.action}">${button.label}</button>
        `).join('');

        container.appendChild(dateQuickInputs);

        dateQuickInputs.addEventListener('click', (evt) => {
            const action = evt.target.getAttribute('data-action');
            let startDate,
                endDate;

            switch (action) {
                case 'last-365-days':
                    startDate = new Date();
                    startDate.setDate(startDate.getDate() - 365);
                    endDate = new Date();
                    break;
                case 'this-year':
                    startDate = new Date(new Date().getFullYear(), 0, 1);
                    endDate = new Date(new Date().getFullYear(), 11, 31);
                    break;
                case 'last-year':
                    startDate = new Date(new Date().getFullYear() - 1, 0, 1);
                    endDate = new Date(new Date().getFullYear() - 1, 11, 31);
                    break;
                case 'year-before-last':
                    startDate = new Date(new Date().getFullYear() - 2, 0, 1);
                    endDate = new Date(new Date().getFullYear() - 2, 11, 31);
                    break;
            }

            instance.setDate([startDate, endDate], true);
            instance.close();

            setCurrentPage(1); // Reset to first page on filter change
        });
    }

    const handleFormChange = (evt) => {
        const {name, value, type, checked} = evt.target;
        if (evt.target.name.includes('[]')) {
            let name = evt.target.name.replace('[]', ''),
                inputs = checked ? [...new Set([...filter[name], value])] : filter[name].filter(v => v !== value);

            setFilter({...filter, [name]: inputs});
        } else {
            setFilter({...filter, [name]: type === 'checkbox' ? checked : value});
        }

        setCurrentPage(1); // Reset to first page on filter change
    }

    const [defaultDate, setDefaultDate] = useState(null);
    let datepickerLocale = null;
    useEffect(() => {
        datepickerLocale = import(`flatpickr/dist/l10n/${_config.lang}.js`).then(module => {
            setDefaultDate([listStartDate, listEndDate])
            return module.default[_config.lang]
        })
    }, [_config.lang]);

    useEffect(() => {
        if (listStartDate && listEndDate) {
            setDefaultDate([listStartDate, listEndDate])
        }
    }, [listStartDate, listEndDate]);


    const datePickerOptions = useMemo(() => ({
        locale: _config.lang,
        mode: 'range',
        showMonths: matchMedia('(max-width: 767px)').matches ? 1 : 2,
        altInput: true,
        maxDate: new Date().setHours(23, 59, 59, 999), // should be today - so no dates in the future
        defaultDate, // may be null or [start, end]
        formatDate: dateObj => formatDate(dateObj, _config.lang),
        onChange: (selectedDates, dateStr, instance) => {
            const endOfToday = new Date();
            endOfToday.setHours(23, 59, 59, 999);

            // Enforce max range of 1 year by restricting selectable dates after the first pick
            if (!selectedDates || selectedDates.length === 0) {
                // cleared
                instance.set('minDate', null);
                instance.set('maxDate', endOfToday); // today
                return;
            }
            if (selectedDates.length === 1) {
                const start = selectedDates[0];
                if (start) {
                    const startAtMidnight = new Date(start);
                    startAtMidnight.setHours(0, 0, 0, 0);

                    const limitStart = new Date(startAtMidnight);
                    limitStart.setFullYear(limitStart.getFullYear() - 2);
                    limitStart.setDate(limitStart.getDate() + 1);

                    const limitEnd = new Date(startAtMidnight);
                    limitEnd.setFullYear(limitEnd.getFullYear() + 2);
                    limitEnd.setDate(limitEnd.getDate() - 1); // inclusive upper bound

                    // Ensure limitEnd does not exceed today
                    if (limitEnd > endOfToday) {
                        limitEnd.setTime(endOfToday.getTime());
                    }
                    console.log('limitStart', limitStart);
                    instance.set('minDate', limitStart);
                    instance.set('maxDate', limitEnd);
                }
                return;
            }

            if (selectedDates.length === 2) {
                const start = selectedDates[0];
                const end = selectedDates[1];
                if (start && end) {
                    const limitEnd = new Date(start);
                    limitEnd.setHours(0, 0, 0, 0);
                    limitEnd.setFullYear(limitEnd.getFullYear() + 2);
                    limitEnd.setDate(limitEnd.getDate() - 1); // inclusive upper bound

                    // Ensure limitEnd does not exceed today
                    if (limitEnd > endOfToday) {
                        limitEnd.setTime(endOfToday.getTime());
                    }

                    if (end > limitEnd) {
                        instance.setDate([start, limitEnd], false);
                    }
                }
                // After a complete selection, allow choosing a new start anywhere in the past
                instance.set('minDate', null);
                instance.set('maxDate', endOfToday); // today
            }
        }
    }), [_config.lang, defaultDate]);

    return (
        <Fragment>
            {fetchingState === FETCHING_STATE.ERROR ? (
                <div className="alert alert-danger">
                    <h5 className="alert-heading">{translate('order-archive.fetching-error')}</h5>
                </div>
            ) : null}

            <div className="d-flex gap-1 text-primary align-items-center justify-content-end">
                <span className={'icon icon-info'}></span> <div className={'wysiwyg'} dangerouslySetInnerHTML={{__html: translate('order-archive.help')}}></div>
            </div>

            <div className="row gy-base align-items-end mt-3">
                <div className="col col-xl-auto" onChange={handleFormChange}>
                    <label htmlFor="search" className={'form-label visually-hidden'}>{translate('order-archive.search-label')}</label>
                    <div className={'position-relative'}>
                        <span className="icon icon-search text-color-default form-control-icon" aria-hidden="true"></span>
                        <input className="form-control" type="text" name={"search"} id={"search"} placeholder={translate('order-archive.search-label')}/>
                    </div>
                </div>
                <div className="col col-xl-auto">
                    <div className={'form-label visually-hidden'}>{translate('order-archive.date-label')}</div>
                    <div className="position-relative">
                        <span className="icon icon-calendar text-color-default form-control-icon" aria-hidden="true"></span>
                        <Flatpickr
                            className={'form-control form-control--datepicker'}
                            options={datePickerOptions}
                            onReady={(selectedDates, dateStr, instance) => {
                                instance.l10ns = {...instance.l10ns, ...datepickerLocale}
                                renderDateQuickInputs(instance.rContainer, instance);
                            }}
                            onClose={(selectedDates, dateStr) => {
                                setCurrentPage(1); // Reset to first page on filter change
                                onChangeListDate({
                                    startDate: selectedDates[0] ? selectedDates[0].getTime() : new Date(new Date().getFullYear(), 0, 1).getTime(),
                                    endDate: selectedDates[1] ? selectedDates[1].getTime() : new Date().getTime()
                                });
                            }}
                        />
                    </div>
                </div>
                <div className="col-md-4">
                    <label htmlFor={'categories'} className={'form-label visually-hidden'}>{translate('order-archive.categories-placeholder')}</label>
                    <div className="position-relative">
                        <span className="icon icon-verfuegbarkeit text-color-default form-control-icon" aria-hidden="true"></span>
                        <Select options={availableCategories}
                                isMulti
                                id={'categories'}
                                name={'categories'}
                                className="form-select-react"
                                classNamePrefix="form-select-react"
                                placeholder={translate('order-archive.categories-placeholder')}
                                noOptionsMessage={() => translate('order-archive.categories-no-options')}
                                onChange={(selected) => {
                                    setCurrentPage(1); // Reset to first page on filter change
                                    setFilter({...filter, categories: selected.map(item => item.value)});
                                }}
                        />
                    </div>
                </div>
            </div>

            <div className="position-relative">
                {fetchingState === FETCHING_STATE.PENDING ? (
                    <Loading headline={translate('loading-spinner.label')} />
                ) : null}

                <div className="table-responsive">
                    <table className="table mt-4">
                        <thead>
                        <tr>
                            <th width={'160px'}><button type={"button"} className={`table__head-sort ${sortField === 'number' && sortDirection === 'desc' ? 'is-sorting-desc' : ''}`} onClick={() => handleSorting('number')}>{translate('order-archive.erp')}  <span className={'icon icon-chevron table__head-sort-icon'} aria-label={'todo'}></span></button></th>
                            <th width={'160px'}><button type={"button"} className={`table__head-sort ${sortField === 'date' && sortDirection === 'desc' ? 'is-sorting-desc' : ''}`} onClick={() => handleSorting('date')}>{translate('order-archive.date')}  <span className={'icon icon-chevron table__head-sort-icon'} aria-label={'todo'}></span></button></th>
                            <th width={'180px'}><button type={"button"} className={`table__head-sort ${sortField === 'submitType' && sortDirection === 'desc' ? 'is-sorting-desc' : ''}`} onClick={() => handleSorting('submitType')}>{translate('order-archive.submit-type')}  <span className={'icon icon-chevron table__head-sort-icon'} aria-label={'todo'}></span></button></th>
                            <th width={'130px'}><button type={"button"} className={`table__head-sort ${sortField === 'requestor' && sortDirection === 'desc' ? 'is-sorting-desc' : ''}`} onClick={() => handleSorting('requestor')}>{translate('order-archive.requestor')}  <span className={'icon icon-chevron table__head-sort-icon'} aria-label={'todo'}></span></button></th>
                            <th><button type={"button"} className={`table__head-sort ${sortField === 'reference' && sortDirection === 'desc' ? 'is-sorting-desc' : ''}`} onClick={() => handleSorting('reference')}>{translate('order-archive.reference')}  <span className={'icon icon-chevron table__head-sort-icon'} aria-label={'todo'}></span></button></th>
                            <th></th>
                        </tr>
                        </thead>
                        {fetchingState === FETCHING_STATE.SUCCESS ? (
                            <tbody>
                            {currentPageItems.map((item, index) => (
                                <TableRowItem item={item} key={item.number + '-' + index} />
                            ))}
                            </tbody>
                        ) : null}
                    </table>
                </div>

                <Pagination totalPages={totalPages} currentPage={currentPage} onChangePage={handleChangePage} />
            </div>
        </Fragment>
    );
};


const mapStateToProps = (state) => {
    return ({
        fetchingState: getListFetchingState(state),
        items: getListItems(state),
        availableCategories: getListAvailableCategories(state),
        listStartDate: getListStartDate(state),
        listEndDate: getListEndDate(state)
    });
};

export const mapDispatchToProps = (dispatch) => {
    return {
        onLoadData: (filter) => dispatch(requireData(filter)),
        onChangeListDate: (date) => dispatch(changeListDate(date))
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(Base)